import RootPortalRef from '@dyl/constants/RootPortalRef';
import useMemoObject from '@dyl/hooks/useMemoObject';
import { Portal } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { uniqBy, without } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import ConnectingLinesContext from './context';

const useStyles = makeStyles(theme => ({
    root: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
    },
    line: {
        strokeWidth: 4,
        stroke: theme.palette.primary.main,
        strokeLinecap: 'round',
        strokeDasharray: theme.spacing(1),
    },
}));

/** @returns {import('./context').CLNode[]} */
const createInitialState = () => [];

export default function ConnectingLinesRenderer({ children }) {
    const classes = useStyles();

    const [nodes, setNodes] = useState(createInitialState);

    const registerNode = useCallback(node => {
        setNodes(prev => {
            let next = [...prev, node];

            next = uniqBy(next, n => `${n.lineId}-${n.side}`);

            return next;
        });

        return () => setNodes(prev => without(prev, node));
    }, []);

    const context = useMemoObject({ registerNode });

    /** @type {[import('./context').CLNode, import('./context').CLNode][]} */
    const lines = useMemo(() => {
        return nodes.reduce((lines, node) => {
            if (node.side === 'start') {
                const end = nodes.find(n => (
                    n.lineId === node.lineId
                    && n.side === 'end'
                ));

                if (end) {
                    lines.push([node, end]);
                }
            }

            return lines;
        }, []);
    }, [nodes]);

    return (
        <>
            <ConnectingLinesContext.Provider value={context}>
                {children}
            </ConnectingLinesContext.Provider>

            <Portal container={RootPortalRef.current}>
                <svg className={classes.root} width="100%" height="100%">
                    {lines.map(([start, end]) => (
                        <line
                            key={start.lineId}
                            className={classes.line}
                            x1={start.position.x}
                            y1={start.position.y}
                            x2={end.position.x}
                            y2={end.position.y}
                        />
                    ))}
                </svg>
            </Portal>
        </>
    );
}